import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import ConfirmDialog from './shared/ConfirmDialog'
import ErrorBanner from './shared/ErrorBanner'
import {
    approvalCheckBlocker,
    isApprovalError,
    isRevisionError,
    variableChanges,
} from '../helpers/whatsappTemplate'
import { errorMessage } from '../services/axios/errorMessage'
import TemplateService from '../services/templateService'
import { RADIUS, SURFACE, TAG, TEXT } from '../theme/tokens'

// Under this score the old app offered the rewrite, and so does this one.
export const RECOMMEND_BELOW = 75

/** `{{a}}, {{b}}` for a list of variable names. */
const named = (variables) => variables.map((v) => `{{${v}}}`).join(', ')

/** A titled list of the analysis' reasons or recommendations. */
const ResultList = ({ title, items }) =>
    items?.length ? (
        <Box sx={{ mt: 1.5 }}>
            <Typography
                variant="overline"
                component="div"
                sx={{ color: TEXT.muted, lineHeight: 1.8 }}
            >
                {title}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {items.map((item, index) => (
                    <Typography
                        component="li"
                        key={index}
                        variant="body2"
                        sx={{ color: TEXT.secondary, mb: 0.5 }}
                    >
                        {String(item)}
                    </Typography>
                ))}
            </Box>
        </Box>
    ) : null

ResultList.propTypes = {
    title: PropTypes.string.isRequired,
    items: PropTypes.array,
}

/**
 * The "Check approval probability" panel under a WhatsApp Utility body.
 *
 * It asks POST /comms/v2/template/approval_probability how likely WhatsApp
 * is to approve the body as a Utility template and shows the score, the
 * category the analysis read, its reasons and its recommendations. Under 75%
 * it offers "Use the recommended message", from POST
 * /comms/v2/template/revised_message.
 *
 * Unlike the old app, the body is sent with its {{variables}} as they are,
 * not with the examples put in their place, so the rewrite comes back with
 * the variables instead of the sample values. When the rewrite drops a
 * variable, the user is asked before the body is replaced (a send would no
 * longer carry that value). The examples live with the form and are keyed by
 * variable, so those of the variables the rewrite keeps stay filled.
 *
 * The check needs a body of 10 or more characters and an example for every
 * variable; until then the button is off and the reason is shown. Both
 * routes answer 200 even when their AI call fails, with an `error`-like
 * category and the original message: that is shown as "Could not check" or
 * "Could not get a recommended message" with the reason, never as a score.
 * A result belongs to the text it was made for, so editing the body clears
 * it.
 */
const ApprovalProbability = ({ body, variables, examples, onUseRevised }) => {
    const dispatch = useDispatch()
    const [checking, setChecking] = useState(false)
    const [result, setResult] = useState(null)
    const [checkError, setCheckError] = useState('')
    const [revising, setRevising] = useState(false)
    const [reviseError, setReviseError] = useState('')
    // A rewrite that drops variables, waiting for the user's yes.
    const [pending, setPending] = useState(null)
    const [notice, setNotice] = useState('')
    // The body this panel just put in, so its own change keeps the notice.
    const replacedWith = useRef(null)

    useEffect(() => {
        setResult(null)
        setCheckError('')
        setReviseError('')
        if (replacedWith.current !== null && body === replacedWith.current) {
            replacedWith.current = null
            return
        }
        setNotice('')
    }, [body])

    const blocker = approvalCheckBlocker({ body, variables, examples })

    const check = () => {
        setChecking(true)
        setCheckError('')
        setReviseError('')
        setNotice('')
        setResult(null)
        dispatch(TemplateService.getApprovalProbability(body)).then((resp) => {
            setChecking(false)
            const answer = resp.error ? null : resp.payload?.data
            if (!answer) {
                setCheckError(
                    `Could not check: ${errorMessage({ data: resp.payload })}`
                )
                return
            }
            if (isApprovalError(answer)) {
                setCheckError(
                    `Could not check: ${
                        answer.reasons?.[0] ?? 'the analysis did not finish.'
                    }`
                )
                return
            }
            setResult(answer)
        })
    }

    /** Put the rewrite in the body and say what changed. */
    const apply = (revised, added) => {
        replacedWith.current = revised
        setPending(null)
        onUseRevised(revised)
        setNotice(
            `Your message was replaced with the recommended one.${
                added.length ? ` Fill in an example for ${named(added)}.` : ''
            } Check it again to see its probability.`
        )
    }

    const recommend = () => {
        setRevising(true)
        setReviseError('')
        dispatch(
            TemplateService.getRevisedMessage({
                message: body,
                percentage: result.percentage,
                reasons: result.reasons ?? [],
                recommendations: result.recommendations ?? [],
            })
        ).then((resp) => {
            setRevising(false)
            const answer = resp.error ? null : resp.payload?.data
            if (!answer || isRevisionError(answer)) {
                setReviseError(
                    `Could not get a recommended message: ${
                        answer
                            ? answer.changes_made?.[0] ?? 'the rewrite failed.'
                            : errorMessage({ data: resp.payload })
                    }`
                )
                return
            }
            const revised = String(answer.revised_message)
            if (revised === body) {
                setNotice('The recommended message is the same as yours.')
                return
            }
            const { dropped, added } = variableChanges(body, revised)
            if (dropped.length) setPending({ revised, dropped, added })
            else apply(revised, added)
        })
    }

    const score = result?.percentage
    const tone = score >= RECOMMEND_BELOW ? TAG.success : TAG.warning

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                border: `1px solid ${SURFACE.border}`,
                borderRadius: `${RADIUS.md}px`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={check}
                    disabled={Boolean(blocker) || checking || revising}
                    startIcon={
                        checking ? (
                            <CircularProgress
                                size={14}
                                sx={{ color: TEXT.muted }}
                            />
                        ) : (
                            <AssessmentOutlinedIcon />
                        )
                    }
                >
                    {checking ? 'Checking…' : 'Check approval probability'}
                </Button>
                <Typography variant="caption" sx={{ color: TEXT.muted }}>
                    {blocker ||
                        'How likely WhatsApp is to approve this body as a Utility template.'}
                </Typography>
            </Box>

            {checkError && (
                <Box sx={{ mt: 1.5 }}>
                    <ErrorBanner message={checkError} />
                </Box>
            )}

            {result && (
                <Box sx={{ mt: 2 }} data-testid="approval-result">
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1.5,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: `${RADIUS.pill}px`,
                                background: tone.bg,
                                color: tone.fg,
                                fontWeight: 700,
                            }}
                        >
                            <Typography
                                component="span"
                                variant="h6"
                                sx={{ color: tone.fg }}
                            >
                                {score}%
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.secondary }}
                        >
                            approval probability
                            {result.category
                                ? `, read as ${String(
                                      result.category
                                  ).toLowerCase()}`
                                : ''}
                        </Typography>
                    </Box>
                    <ResultList title="Reasons" items={result.reasons} />
                    <ResultList
                        title="Recommendations"
                        items={result.recommendations}
                    />
                    {score < RECOMMEND_BELOW && (
                        <Box
                            sx={{
                                mt: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={recommend}
                                disabled={revising}
                                startIcon={
                                    revising ? (
                                        <CircularProgress
                                            size={14}
                                            sx={{ color: TEXT.muted }}
                                        />
                                    ) : (
                                        <AutoFixHighIcon />
                                    )
                                }
                            >
                                {revising
                                    ? 'Getting the recommendation…'
                                    : 'Use the recommended message'}
                            </Button>
                            <Typography
                                variant="caption"
                                sx={{ color: TEXT.muted }}
                            >
                                Rewrites the body to follow the Utility rules;
                                you can still edit it before you submit.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {reviseError && (
                <Box sx={{ mt: 1.5 }}>
                    <ErrorBanner message={reviseError} />
                </Box>
            )}

            {notice && (
                <Typography
                    role="status"
                    variant="body2"
                    sx={{ mt: 1.5, color: TEXT.secondary }}
                >
                    {notice}
                </Typography>
            )}

            <ConfirmDialog
                open={Boolean(pending)}
                title="Use the recommended message?"
                message={
                    pending
                        ? `The recommended message drops ${named(
                              pending.dropped
                          )}, so a send would no longer carry ${
                              pending.dropped.length === 1
                                  ? 'that value'
                                  : 'those values'
                          }. Replace your message anyway?`
                        : ''
                }
                confirmLabel="Replace my message"
                onConfirm={() => apply(pending.revised, pending.added)}
                onCancel={() => setPending(null)}
            />
        </Box>
    )
}

ApprovalProbability.propTypes = {
    // The body as typed, {{variables}} included.
    body: PropTypes.string.isRequired,
    // The body's {{variables}}, in order.
    variables: PropTypes.arrayOf(PropTypes.string).isRequired,
    // Each variable's example value, by name.
    examples: PropTypes.object.isRequired,
    // Called with the rewrite the user chose to use.
    onUseRevised: PropTypes.func.isRequired,
}

export default ApprovalProbability
